import { InfluxDB, Point } from "@influxdata/influxdb-client";

class InfluxDBService {
    constructor() {
        // Configure your InfluxDB connection
        this.url = "http://localhost:8086";
        this.token = "my-super-secret-token";
        this.org = "myorg";
        this.bucket = "iot_data";

        // Keep the client instance for later use if needed
        this.client = new InfluxDB({
            url: this.url,
            token: this.token,
        });
    }

    async query(fluxQuery) {
        try {
            // console.log("InfluxDB Query Debug:", {
            //     url: `${this.url}/api/v2/query?org=${this.org}`,
            //     query: fluxQuery,
            //     token: this.token ? "Token present" : "No token",
            //     org: this.org,
            //     bucket: this.bucket
            // });

            // Try direct fetch first
            const response = await fetch(`${this.url}/api/v2/query?org=${this.org}`, {
                method: "POST",
                headers: {
                    Authorization: `Token ${this.token}`,
                    "Content-Type": "application/vnd.flux",
                    Accept: "application/csv",
                },
                body: fluxQuery,
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Error response:", errorText);
                throw new Error(
                    `HTTP error! status: ${response.status}, message: ${errorText}`
                );
            }

            const data = await response.text();
            //    console.log("Raw response data:", data.substring(0, 500) + "..."); // Log first 500 chars

            // Parse CSV response
            const rows = data
                .split("\n")
                .filter((line) => line.trim() && !line.startsWith("#"))
                .map((line) => {
                    const values = line.split(",");
                    return values;
                });

            console.log(
                "Parsed rows:",
                rows.length,
                rows.length > 0 ? rows[0] : "No rows"
            );

            // Return empty array for empty results
            if (rows.length === 0) {
                console.log("No rows returned from InfluxDB");
                return [];
            }

            // Format data for store
            try {
                // Extract headers from first row
                const headers = rows[0];
                // Create a formatted result that matches what the store expects
                const formattedResult = {
                    result: [{
                        header: headers.map((label, index) => ({ label, index })),
                        table: rows.slice(1),
                    }, ],
                };

                console.log("Formatted result for store:", formattedResult);
                return [formattedResult];
            } catch (formatError) {
                console.error("Error formatting InfluxDB response:", formatError);
                // Return raw rows if formatting fails
                return rows;
            }
        } catch (error) {
            console.error("Query error details:", {
                message: error.message,
                url: `${this.url}/api/v2/query?org=${this.org}`,
                query: fluxQuery,
                stack: error.stack,
            });
            throw error;
        }
    }

    // Example method to write data
    async writeData(measurement, tags, fields) {
        try {
            const writeApi = this.client.getWriteApi(this.org, this.bucket);
            const point = new Point(measurement);

            // Add tags
            Object.entries(tags).forEach(([key, value]) => {
                point.tag(key, value);
            });

            // Add fields
            Object.entries(fields).forEach(([key, value]) => {
                point.floatField(key, value);
            });

            await writeApi.writePoint(point);
            await writeApi.close();

            return true;
        } catch (error) {
            console.error("Error writing to InfluxDB:", error);
            throw error;
        }
    }

    async queryGyroData(
        deviceId,
        timeRange = {},
        windowPeriod = "5s",
        isDemo = false
    ) {
        console.log("InfluxDBService.queryGyroData called with:", {
            deviceId,
            timeRange,
            windowPeriod,
            isDemo,
        });

        // Update static dates to match our requirements
        let defaultStart, defaultStop;

        // Use isDemo flag passed from component instead of checking deviceId
        if (isDemo) {
            // Demo user: Feb 1-28, 2025
            defaultStart = "2025-02-01T00:00:00.000Z";
            defaultStop = "2025-02-28T23:59:59.000Z";
        } else {
            // Regular user: Mar 24-27, 2025
            defaultStart = "2025-03-24T00:00:00.000Z";
            defaultStop = "2025-03-27T23:59:59.000Z";
        }

        const start = timeRange.start || defaultStart;
        const stop = timeRange.stop || defaultStop;

        console.log("Using date range:", { start, stop });

        // Handle multiple devices when deviceId is an array
        let deviceFilter;
        if (Array.isArray(deviceId)) {
            // Create a filter for multiple devices
            deviceFilter = deviceId
                .map((id) => `r["device"] == "${id}"`)
                .join(" or ");
        } else {
            // Single device filter
            deviceFilter = `r["device"] == "${deviceId}"`;
        }

        // Log the headers and fields to help debug
        const query = `
            from(bucket: "${this.bucket}")
                |> range(start: time(v: "${start}"), stop: time(v: "${stop}"))
                |> filter(fn: (r) => r["_measurement"] == "inertial_data")
                |> filter(fn: (r) => r["_field"] == "accel_x" or r["_field"] == "accel_y" or r["_field"] == "accel_z" or r["_field"] == "gyro_x" or r["_field"] == "gyro_y" or r["_field"] == "gyro_z" or r["_field"] == "temperature" or r["_field"] == "percentage" or r["_field"] == "temp" or r["_field"] == "batt")
                |> filter(fn: (r) => r["data_type"] == "inertial")
                |> filter(fn: (r) => ${deviceFilter})
                |> aggregateWindow(every: ${windowPeriod}, fn: mean, createEmpty: false)
                |> pivot(rowKey:["_time"], columnKey: ["_field"], valueColumn: "_value")
                |> yield(name: "mean")
        `;

        // Log the query to console and terminal
        console.log("InfluxDB Query:", query);
        console.warn("INFLUXDB QUERY:", query); // This will be more visible in browser console

        try {
            const result = await this.query(query);
            console.log(
                "InfluxDB raw result received:",
                result ? result.length : 0,
                "rows"
            );
            if (result && result.length > 0) {
                console.log("First row:", result[0]);
            } else {
                console.log("No data received from InfluxDB");
            }

            // The query method already transforms the data into the correct format for the store
            // No need for additional transformation
            return result;
        } catch (error) {
            console.error("Error in queryGyroData:", error);
            throw error;
        }
    }

    async queryAccelData(
        deviceId,
        timeRange = {},
        windowPeriod = "5s",
        isDemo = false
    ) {
        // Update static dates to match our requirements
        let defaultStart, defaultStop;

        // Use isDemo flag passed from component instead of checking deviceId
        if (isDemo) {
            // Demo user: Feb 1-28, 2025
            defaultStart = "2025-02-01T00:00:00.000Z";
            defaultStop = "2025-02-28T23:59:59.000Z";
        } else {
            // Regular user: Mar 24-27, 2025
            defaultStart = "2025-03-24T00:00:00.000Z";
            defaultStop = "2025-03-27T23:59:59.000Z";
        }

        const start = timeRange.start || defaultStart;
        const stop = timeRange.stop || defaultStop;

        // Handle multiple devices when deviceId is an array
        let deviceFilter;
        if (Array.isArray(deviceId)) {
            // Create a filter for multiple devices
            deviceFilter = deviceId
                .map((id) => `r["device"] == "${id}"`)
                .join(" or ");
        } else {
            // Single device filter
            deviceFilter = `r["device"] == "${deviceId}"`;
        }

        // Log the headers and fields to help debug
        const query = `
      from(bucket: "${this.bucket}")
        |> range(start: time(v: "${start}"), stop: time(v: "${stop}"))
        |> filter(fn: (r) => r["_measurement"] == "inertial_data")
        |> filter(fn: (r) => r["_field"] == "accel_x" or r["_field"] == "accel_y" or r["_field"] == "accel_z" or r["_field"] == "gyro_x" or r["_field"] == "gyro_y" or r["_field"] == "gyro_z" or r["_field"] == "temperature" or r["_field"] == "percentage" or r["_field"] == "temp" or r["_field"] == "batt")
        |> filter(fn: (r) => r["data_type"] == "inertial")
        |> filter(fn: (r) => ${deviceFilter})
        |> aggregateWindow(every: ${windowPeriod}, fn: mean, createEmpty: false)
        |> pivot(rowKey:["_time"], columnKey: ["_field"], valueColumn: "_value")
        |> yield(name: "mean")
    `;

        // Log the query to console and terminal
        console.log("InfluxDB Query:", query);
        // console.warn("INFLUXDB QUERY:", query); // This will be more visible in browser console

        return this.query(query);
    }
}

export default new InfluxDBService();